import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Row,
  Select,
  Spin,
  Table,
  Tabs,
  Tag,
} from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  GlobalOutlined,
  HistoryOutlined,
  TeamOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { post } from "../../../services/services";
import { ApiURL } from "../../../services/apiConstants";
import ViolationBadge from "../../../components/ViolationBadge";
import { VIOLATION_CONFIG } from "../../../config/violationConfig";

/* ── constants ─────────────────────────────────────────────────── */
const REGULATION_LABEL = {
  MLC_2006:          "MLC 2006",
  STCW_A_VIII_1:     "STCW VIII/1",
  MANILA_AMENDMENT:  "Manila",
  MLC_STANDARD_A1_1: "MLC A1.1",
};
const REGULATION_COLOR = {
  MLC_2006:          "blue",
  STCW_A_VIII_1:     "purple",
  MANILA_AMENDMENT:  "orange",
  MLC_STANDARD_A1_1: "cyan",
};

const violColor = (n) => (n === 0 ? "#52c41a" : n <= 5 ? "#fa8c16" : "#f5222d");

/* ── sub-components ─────────────────────────────────────────────── */
const RegTag = ({ reg }) => (
  <Tag color={REGULATION_COLOR[reg] || "default"} style={{ fontSize: 11 }}>
    {REGULATION_LABEL[reg] || reg}
  </Tag>
);

const StatusTag = ({ status }) => (
  <Tag color={status === "acknowledged" ? "success" : "processing"} style={{ textTransform: "capitalize" }}>
    {status}
  </Tag>
);

/* ── Fleet ship card ─────────────────────────────────────────────── */
const ShipCard = ({ ship }) => (
  <Card
    style={{
      borderRadius: 14,
      border: `1.5px solid ${ship.is_compliant ? "#b7eb8f" : "#ffccc7"}`,
      boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
      height: "100%",
    }}
    styles={{ body: { padding: 16 } }}
  >
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{ship.ship_name}</div>
        <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
          <TeamOutlined style={{ marginRight: 4 }} />
          {ship.crew_count} crew
        </div>
      </div>
      {ship.is_compliant ? (
        <CheckCircleFilled style={{ color: "#52c41a", fontSize: 20 }} />
      ) : (
        <CloseCircleFilled style={{ color: "#f5222d", fontSize: 20 }} />
      )}
    </div>

    <div style={{ display: "flex", gap: 16 }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: violColor(ship.active_violations), lineHeight: 1 }}>
          {ship.active_violations}
        </div>
        <div style={{ fontSize: 11, color: "#888" }}>Active</div>
      </div>
      <div style={{ width: 1, background: "#f0f0f0" }} />
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: "#52c41a", lineHeight: 1 }}>
          {ship.acknowledged_violations}
        </div>
        <div style={{ fontSize: 11, color: "#888" }}>Acknowledged</div>
      </div>
    </div>
  </Card>
);

/* ── shared violation table columns ─────────────────────────────── */
const violationColumns = (showShip, extra = []) => [
  {
    title: "Date", dataIndex: "date", key: "date", width: 110, align: "center",
    sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
    render: (d) => dayjs(d).format("DD MMM YYYY"),
  },
  ...(showShip
    ? [{ title: "Ship", dataIndex: "ship_name", key: "ship_name", align: "center", width: 140 }]
    : []),
  { title: "Crew Member", dataIndex: "staff_name", key: "staff_name", align: "center", width: 140 },
  {
    title: "Violation", key: "violation", align: "left", minWidth: 260,
    render: (_, r) => <ViolationBadge rule={r.rule} detail={r.detail} />,
  },
  {
    title: "Regulation", dataIndex: "regulation", key: "regulation", align: "center", width: 120,
    render: (reg) => <RegTag reg={reg} />,
  },
  {
    title: "Status", dataIndex: "status", key: "status", align: "center", width: 120,
    render: (s) => <StatusTag status={s} />,
  },
  ...extra,
];

/* ── main component ─────────────────────────────────────────────── */
const ViolationsCom = () => {
  const [ships, setShips] = useState([]);

  /* Fleet tab */
  const [fleetLoading, setFleetLoading] = useState(false);
  const [fleetData, setFleetData]       = useState(null);
  const [fleetMonth, setFleetMonth]     = useState(dayjs());

  /* Escalation tab */
  const [escLoading, setEscLoading]   = useState(false);
  const [escData, setEscData]         = useState([]);
  const [escPag, setEscPag]           = useState({ current: 1, pageSize: 20, total: 0 });
  const [escShipId, setEscShipId]     = useState(null);
  const [escThreshold, setEscThreshold] = useState(24);

  /* History tab */
  const [histLoading, setHistLoading] = useState(false);
  const [histData, setHistData]       = useState([]);
  const [histPag, setHistPag]         = useState({ current: 1, pageSize: 20, total: 0 });
  const [histFilters, setHistFilters] = useState({
    ship_id: "", staff_id: "", rule: "", start_date: "", end_date: "",
    severity: "", status: "", regulation: "",
  });

  useEffect(() => {
    fetchShips();
    fetchFleet(dayjs());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchShips = () => {
    post(ApiURL.list_ships_view_com, new FormData()).then((res) => {
      if (res?.status === true) setShips(res.ships || []);
    });
  };

  const fetchFleet = (month) => {
    setFleetLoading(true);
    const fd = new FormData();
    fd.append("year",  month.format("YYYY"));
    fd.append("month", month.format("M"));
    post(ApiURL.violations_fleet_com, fd).then((res) => {
      setFleetData(res?.status === true ? res.data || null : null);
      setFleetLoading(false);
    });
  };

  const fetchEscalation = (page = 1, shipId = escShipId, threshold = escThreshold) => {
    setEscLoading(true);
    const fd = new FormData();
    if (shipId)   fd.append("ship_id",         shipId);
    if (threshold) fd.append("threshold_hours", threshold);
    fd.append("page",      page);
    fd.append("page_size", escPag.pageSize);
    post(ApiURL.violations_unresolved_com, fd).then((res) => {
      setEscData(res?.status === true ? res.data || [] : []);
      if (res?.pagination)
        setEscPag((p) => ({ ...p, current: res.pagination.page, total: res.pagination.total }));
      setEscLoading(false);
    });
  };

  const fetchHistory = (page = 1, filters = histFilters) => {
    setHistLoading(true);
    const fd = new FormData();
    if (filters.ship_id)    fd.append("ship_id",    filters.ship_id);
    if (filters.staff_id)   fd.append("staff_id",   filters.staff_id);
    if (filters.rule)       fd.append("rule",        filters.rule);
    if (filters.start_date) fd.append("start_date", filters.start_date);
    if (filters.end_date)   fd.append("end_date",   filters.end_date);
    if (filters.severity)   fd.append("severity",   filters.severity);
    if (filters.status)     fd.append("status",     filters.status);
    if (filters.regulation) fd.append("regulation", filters.regulation);
    fd.append("page",      page);
    fd.append("page_size", histPag.pageSize);
    post(ApiURL.violations_history_com, fd).then((res) => {
      setHistData(res?.status === true ? res.data || [] : []);
      if (res?.pagination)
        setHistPag((p) => ({ ...p, current: res.pagination.page, total: res.pagination.total }));
      setHistLoading(false);
    });
  };

  const fleet = fleetData?.fleet || [];
  const nonCompliant = fleet.filter((s) => !s.is_compliant).length;

  return (
    <Tabs
      defaultActiveKey="fleet"
      style={{ marginTop: 12 }}
      items={[
        /* ── Fleet Dashboard ── */
        {
          key: "fleet",
          label: <span><GlobalOutlined /> Fleet Dashboard</span>,
          children: (
            <>
              {/* Month picker + search */}
              <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 16, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Month</div>
                  <DatePicker
                    picker="month"
                    value={fleetMonth}
                    format="MMMM YYYY"
                    style={{ width: 160 }}
                    disabledDate={(c) => c && c > dayjs().endOf("month")}
                    onChange={(v) => { if (v) setFleetMonth(v); }}
                  />
                </div>
                <Button type="primary" loading={fleetLoading} onClick={() => fetchFleet(fleetMonth)}>
                  Search
                </Button>
              </div>

              {fleetLoading ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}><Spin size="large" /></div>
              ) : fleetData ? (
                <>
                  {/* Summary stat cards */}
                  <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                    <Col xs={12} sm={8} md={6}>
                      <Card style={{ borderRadius: 12, border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}
                        styles={{ body: { padding: "14px 18px" } }}>
                        <div style={{ fontSize: 24, fontWeight: 800, color: "#1890ff" }}>{fleetData.total_ships}</div>
                        <div style={{ fontSize: 12, color: "#888" }}>Total Ships</div>
                      </Card>
                    </Col>
                    <Col xs={12} sm={8} md={6}>
                      <Card style={{ borderRadius: 12, border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}
                        styles={{ body: { padding: "14px 18px" } }}>
                        <div style={{ fontSize: 24, fontWeight: 800, color: nonCompliant > 0 ? "#f5222d" : "#52c41a" }}>
                          {nonCompliant}
                        </div>
                        <div style={{ fontSize: 12, color: "#888" }}>Non-Compliant Ships</div>
                      </Card>
                    </Col>
                    <Col xs={12} sm={8} md={6}>
                      <Card style={{ borderRadius: 12, border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}
                        styles={{ body: { padding: "14px 18px" } }}>
                        <div style={{ fontSize: 24, fontWeight: 800, color: "#52c41a" }}>
                          {fleet.length - nonCompliant}
                        </div>
                        <div style={{ fontSize: 12, color: "#888" }}>Compliant Ships</div>
                      </Card>
                    </Col>
                    <Col xs={12} sm={8} md={6}>
                      <Card style={{ borderRadius: 12, border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}
                        styles={{ body: { padding: "14px 18px" } }}>
                        <div style={{ fontSize: 24, fontWeight: 800, color: violColor(fleet.reduce((s, sh) => s + sh.active_violations, 0)) }}>
                          {fleet.reduce((s, sh) => s + sh.active_violations, 0)}
                        </div>
                        <div style={{ fontSize: 12, color: "#888" }}>Total Active Violations</div>
                      </Card>
                    </Col>
                  </Row>

                  {/* Ship cards */}
                  <Row gutter={[12, 12]}>
                    {fleet.map((ship) => (
                      <Col key={ship.ship_id} xs={24} sm={12} md={8} lg={6}>
                        <ShipCard ship={ship} />
                      </Col>
                    ))}
                  </Row>
                </>
              ) : (
                <div style={{ textAlign: "center", color: "#aaa", padding: "40px 0" }}>
                  Select a month and click Search.
                </div>
              )}
            </>
          ),
        },

        /* ── Escalation Queue ── */
        {
          key: "escalation",
          label: (
            <span>
              <WarningOutlined />
              {" Escalation Queue"}
              {escData.length > 0 && (
                <Badge count={escData.length} size="small" style={{ marginLeft: 6, backgroundColor: "#f5222d" }} />
              )}
            </span>
          ),
          children: (
            <>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Ship</div>
                  <Select
                    placeholder="All ships"
                    allowClear
                    style={{ width: 180 }}
                    value={escShipId}
                    onChange={(v) => setEscShipId(v || null)}
                  >
                    {ships.map((s) => (
                      <Select.Option key={s.id} value={s.id}>{s.ship_name}</Select.Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Unresolved ≥ (hours)</div>
                  <Input
                    type="number"
                    min={1}
                    value={escThreshold}
                    style={{ width: 100 }}
                    onChange={(e) => setEscThreshold(Number(e.target.value) || 24)}
                  />
                </div>
                <Button type="primary" onClick={() => fetchEscalation(1)} loading={escLoading}>
                  Search
                </Button>
              </div>

              <Table
                bordered
                size="small"
                columns={[
                  ...violationColumns(true, [
                    {
                      title: "Hours Unresolved", dataIndex: "hours_unresolved", key: "hrs", align: "center", width: 140,
                      sorter: (a, b) => b.hours_unresolved - a.hours_unresolved,
                      render: (h) => (
                        <Tag color={h >= 48 ? "error" : h >= 24 ? "warning" : "default"}>
                          {Number(h).toFixed(1)}h
                        </Tag>
                      ),
                    },
                  ]),
                ]}
                dataSource={escData.map((r, i) => ({ ...r, key: r.id ?? i }))}
                loading={escLoading}
                pagination={{
                  current: escPag.current,
                  pageSize: escPag.pageSize,
                  total: escPag.total,
                  showTotal: (t) => `${t} unresolved violations`,
                  onChange: (p) => fetchEscalation(p),
                }}
                className="custom-table"
                locale={{ emptyText: "No unresolved violations above threshold" }}
                scroll={{ x: 900 }}
                rowClassName={(r) => (r.hours_unresolved >= 48 ? "ant-table-row-danger" : "")}
              />
            </>
          ),
        },

        /* ── History ── */
        {
          key: "history",
          label: <span><HistoryOutlined /> History</span>,
          children: (
            <>
              <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                <div>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Ship</div>
                  <Select
                    placeholder="All ships"
                    allowClear
                    style={{ width: 160 }}
                    onChange={(v) => setHistFilters((p) => ({ ...p, ship_id: v || "" }))}
                  >
                    {ships.map((s) => (
                      <Select.Option key={s.id} value={s.id}>{s.ship_name}</Select.Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Staff ID</div>
                  <Input
                    placeholder="Staff ID"
                    style={{ width: 100 }}
                    onChange={(e) => setHistFilters((p) => ({ ...p, staff_id: e.target.value }))}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Date Range</div>
                  <DatePicker.RangePicker
                    format="YYYY-MM-DD"
                    disabledDate={(c) => c && c > dayjs().endOf("day")}
                    onChange={(v) =>
                      setHistFilters((p) => ({
                        ...p,
                        start_date: v?.[0]?.format("YYYY-MM-DD") || "",
                        end_date:   v?.[1]?.format("YYYY-MM-DD") || "",
                      }))
                    }
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Rule</div>
                  <Select
                    placeholder="All rules"
                    allowClear
                    style={{ width: 230 }}
                    onChange={(v) => setHistFilters((p) => ({ ...p, rule: v || "" }))}
                    options={Object.entries(VIOLATION_CONFIG).map(([r, c]) => ({ label: c.label, value: r }))}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Status</div>
                  <Select
                    placeholder="All"
                    allowClear
                    style={{ width: 140 }}
                    onChange={(v) => setHistFilters((p) => ({ ...p, status: v || "" }))}
                    options={[
                      { label: "Active", value: "active" },
                      { label: "Acknowledged", value: "acknowledged" },
                    ]}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Severity</div>
                  <Select
                    placeholder="All"
                    allowClear
                    style={{ width: 120 }}
                    onChange={(v) => setHistFilters((p) => ({ ...p, severity: v || "" }))}
                    options={[
                      { label: "Violation", value: "violation" },
                      { label: "Warning", value: "warning" },
                    ]}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Regulation</div>
                  <Select
                    placeholder="All"
                    allowClear
                    style={{ width: 140 }}
                    onChange={(v) => setHistFilters((p) => ({ ...p, regulation: v || "" }))}
                    options={Object.entries(REGULATION_LABEL).map(([k, v]) => ({ label: v, value: k }))}
                  />
                </div>
                <Button type="primary" onClick={() => fetchHistory(1)} loading={histLoading}>
                  Search
                </Button>
              </div>

              <Table
                bordered
                size="small"
                columns={[
                  ...violationColumns(true, [
                    {
                      title: "Acknowledged At", dataIndex: "acknowledged_at", key: "ack_at", align: "center", width: 155,
                      render: (v) => v ? dayjs(v).format("DD MMM YYYY HH:mm") : <span style={{ color: "#bbb" }}>—</span>,
                    },
                    {
                      title: "Corrective Action", dataIndex: "corrective_action", key: "corrective_action", align: "left",
                      render: (v) => v || <span style={{ color: "#bbb" }}>—</span>,
                    },
                  ]),
                ]}
                dataSource={histData.map((r, i) => ({ ...r, key: r.id ?? i }))}
                loading={histLoading}
                pagination={{
                  current: histPag.current,
                  pageSize: histPag.pageSize,
                  total: histPag.total,
                  showTotal: (t) => `${t} records`,
                  onChange: (p) => fetchHistory(p),
                }}
                className="custom-table"
                locale={{ emptyText: "Apply filters and click Search to load history" }}
                scroll={{ x: 1000 }}
              />
            </>
          ),
        },
      ]}
    />
  );
};

export default ViolationsCom;
